import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { AxiosResponse } from 'axios';
import { PersonOfInterest, DriverLicense } from '../types';
import PersonModal from './PersonModal';
import AddPersonModal from './AddPersonModal';
import { usePermissions } from '../context/PermissionsContext';

const API_URL = process.env.REACT_APP_API_URL || '';


interface PersonOfInterestTabProps {
  clientId?: string;
}

interface PersonOfInterestErrors {
  name: string;
  surname: string;
  email: string;
  designation: string;
  status: string;
  contact_number: string;
  id_passport_number: string;
  date_of_birth: string;
  drivers_license_codes: string;
  server: string;
}

const parseDriverLicenses = (licenseData: any): DriverLicense[] => {
  console.log('🔍 parseDriverLicenses input:', licenseData);
  
  if (!licenseData || licenseData === 'null' || licenseData === 'undefined' || licenseData === null) {
    return [];
  }
  
  try {
    // If it's already an array, return it
    if (Array.isArray(licenseData)) {
      return licenseData;
    }
    
    // If it's a string with pipe format (code|date|expiry)
    if (typeof licenseData === 'string' && licenseData.includes('|')) {
      const licensePairs = licenseData.split(',');
      const result = licensePairs.map(pair => {
        const [code, dateOfFirstIssue, expiresOn] = pair.split('|');
        const license = {
          code: code || '',
          dateOfFirstIssue: dateOfFirstIssue || '',
          expiresOn: expiresOn || ''
        };
        console.log('🔍 Parsed license from string:', license);
        return license;
      }).filter(license => license.code && license.code.trim() !== '');
      
      console.log('🔍 Final parsed licenses from string:', result);
      return result;
    }
    
    // If it's a string containing JSON
    if (typeof licenseData === 'string') {
      try {
        const parsed = JSON.parse(licenseData);
        return Array.isArray(parsed) ? parsed : [parsed];
      } catch (jsonError) {
        // If JSON parsing fails, treat as single code
        return [{
          code: licenseData.trim(),
          dateOfFirstIssue: '',
          expiresOn: ''
        }];
      }
    }
    
    // If it's an object, wrap it in an array
    if (typeof licenseData === 'object' && licenseData !== null) {
      return [licenseData];
    }
    
    return [];
  } catch (error) {
    console.error('❌ Error parsing driver licenses:', error);
    console.error('❌ Problematic license data:', licenseData);
    return [];
  }
};

// Add this helper function after parseDriverLicenses
const formatDisplayDate = (dateString: string | undefined): string => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting display date:', error);
    return 'N/A';
  }
};

const PersonOfInterestTab = ({ clientId }: PersonOfInterestTabProps) => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [personsOfInterest, setPersonsOfInterest] = useState<PersonOfInterest[]>([]);
  const [isLoadingPersons, setIsLoadingPersons] = useState(false);
  const [isPersonModalOpen, setIsPersonModalOpen] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<PersonOfInterest | null>(null);
  const [editingPersonId, setEditingPersonId] = useState<number | null>(null);
  const [newPerson, setNewPerson] = useState<Partial<PersonOfInterest>>({
    id: 0,
    name: '',
    surname: '',
    email: '',
    designation: '',
    status: 'Active',
    contact_number: '+27',
    id_passport_number: '',
    date_of_birth: '',
    drivers_license_codes: [],
  });
  const [personErrors, setPersonErrors] = useState<PersonOfInterestErrors>({
    name: '',
    surname: '',
    email: '',
    designation: '',
    status: '',
    contact_number: '',
    id_passport_number: '',
    date_of_birth: '',
    drivers_license_codes: '',
    server: '',
  });

  const [usedLicenseCodes, setUsedLicenseCodes] = useState<string[]>([]);

  // Function to extract all used license codes
  const getAllUsedLicenseCodes = (people: PersonOfInterest[], excludePersonId?: number | null): string[] => {
    const allLicenses: string[] = [];
    
    people.forEach(person => {
      // Skip the person we're currently editing (if any)
      if (person.id !== excludePersonId && person.drivers_license_codes) {
        person.drivers_license_codes.forEach(license => {
          if (license.code && license.code.trim() !== '') {
            allLicenses.push(license.code);
          }
        });
      }
    });
    
    return allLicenses;
  };

  // Update the refreshPersonsList function to show more details about each person
const refreshPersonsList = async () => {
  if (!clientId || isNaN(parseInt(clientId))) {
    console.error('❌ refreshPersonsList: Invalid Client ID');
    return;
  }
  
  try {
    setIsLoadingPersons(true);
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token found');
    
    console.log('🔄 refreshPersonsList: Fetching persons...');
    
    const response: AxiosResponse<any[]> = await axios.get(
      `${API_URL}/api/persons-of-interest?clientId=${clientId}`,
      {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000
      }
    );
    
    console.log('🔄 refreshPersonsList - Raw API response:', response.data);
    console.log('🔄 refreshPersonsList - Number of persons:', response.data.length);
    
    if (!response.data || !Array.isArray(response.data)) {
      console.error('❌ refreshPersonsList: Invalid response format');
      setPersonsOfInterest([]);
      return;
    }
    
    // Show full details of each person including the new one
    response.data.forEach((person, index) => {
      console.log(`👤 Person ${index + 1} FULL DATA:`, person);
    });
    
    // Map and parse the data with better debugging
    const mappedPersons = response.data.map((person, index) => {
      console.log(`🔄 Processing person ${index + 1}:`, person.id, person.name);
      console.log('🔄 ALL PERSON FIELDS:', Object.keys(person));
      console.log('🔄 Raw driver_license_code:', person.drivers_license_code);
      console.log('🔄 Raw driver_license_codes:', person.drivers_license_codes); // Check if plural exists
      
      const parsedLicenses = parseDriverLicenses(person.drivers_license_code);
      console.log('🔄 Parsed licenses:', parsedLicenses);
      
      return {
        ...person,
        drivers_license_codes: parsedLicenses,
        licensesCount: parsedLicenses.length
      };
    });
    
    console.log('🔄 Final mapped persons:', mappedPersons);
    setPersonsOfInterest(mappedPersons);
    setPersonErrors((prev) => ({ ...prev, server: '' }));
  } catch (error: any) {
    console.error('❌ Error refreshing persons list:', error);
    console.error('❌ Error response:', error.response?.data);
    console.error('❌ Error status:', error.response?.status);
    
    setPersonErrors((prev) => ({
      ...prev,
      server: `Failed to refresh persons list: ${error.response?.data?.message || error.message}`,
    }));
    setPersonsOfInterest([]);
  } finally {
    setIsLoadingPersons(false);
  }
};

  // Fetch persons of interest
useEffect(() => {
  if (!clientId || isNaN(parseInt(clientId))) {
    setPersonErrors((prev) => ({ ...prev, server: 'Invalid Client ID' }));
    return;
  }
  const fetchPersonsOfInterest = async () => {
    setIsLoadingPersons(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response: AxiosResponse<any[]> = await axios.get(
        `${API_URL}/api/persons-of-interest?clientId=${clientId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      // Map from backend field name to frontend field name and parse JSON
      const mappedPersons = response.data.map(person => {
        // Use the parseDriverLicenses function
        const parsedLicenses = parseDriverLicenses(person.drivers_license_code);
        
        return {
          ...person,
          drivers_license_codes: parsedLicenses,
          licensesCount: parsedLicenses.length
        };
      });
      
      setPersonsOfInterest(mappedPersons);
      setPersonErrors((prev) => ({ ...prev, server: '' }));
    } catch (error: any) {
      console.error('Error fetching persons of interest:', error);
      setPersonErrors((prev) => ({
        ...prev,
        server:
          error.response?.status === 404
            ? 'Persons of interest endpoint not found. Please check the backend.'
            : `Failed to load persons of interest: ${error.response?.data?.message || error.message}`,
      }));
      setPersonsOfInterest([]);
    } finally {
      setIsLoadingPersons(false);
    }
  };
  fetchPersonsOfInterest();
}, [clientId]);

  // Update used licenses when personsOfInterest changes
  useEffect(() => {
    const usedCodes = getAllUsedLicenseCodes(personsOfInterest, editingPersonId);
    setUsedLicenseCodes(usedCodes);
  }, [personsOfInterest, editingPersonId]);

  // Validate person form
  const validatePersonForm = () => {
  let isValid = true;
  const newErrors: PersonOfInterestErrors = {
    name: '',
    surname: '',
    email: '',
    designation: '',
    status: '',
    contact_number: '',
    id_passport_number: '',
    date_of_birth: '',
    drivers_license_codes: '',
    server: '',
  };

  if (!newPerson.name?.trim()) {
    newErrors.name = 'Name is required';
    isValid = false;
  }
  if (!newPerson.surname?.trim()) {
    newErrors.surname = 'Surname is required';
    isValid = false;
  }
  if (!newPerson.email?.trim()) {
    newErrors.email = 'Email is required';
    isValid = false;
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newPerson.email)) {
    newErrors.email = 'Invalid email format';
    isValid = false;
  }
  if (!newPerson.designation?.trim()) {
    newErrors.designation = 'Designation is required';
    isValid = false;
  }
  if (!newPerson.status?.trim()) {
    newErrors.status = 'Status is required';
    isValid = false;
  }
  
  // FIXED: Contact number validation
  if (newPerson.contact_number && newPerson.contact_number !== '+27') {
    // Remove the +27 and count only the actual digits
    const digitsOnly = newPerson.contact_number.replace('+27', '').replace(/\D/g, '');
    console.log('🔢 Contact number validation:', {
      original: newPerson.contact_number,
      digitsOnly: digitsOnly,
      length: digitsOnly.length
    });
    
    // South African mobile numbers should be 9 digits after +27
    if (digitsOnly.length !== 9) {
      newErrors.contact_number = 'South African mobile number must be 9 digits after +27 (e.g., +27831234567)';
      isValid = false;
    }
  }
  
  setPersonErrors(newErrors);
  
  // Log validation details for debugging
  console.log('📋 Validation details:', {
    isValid: isValid,
    errors: newErrors,
    contactNumber: newPerson.contact_number
  });
  
  return isValid;
};

  const handleAddOrUpdatePerson = async (e: React.FormEvent) => {
  e.preventDefault();

  console.log('🚀 START: handleAddOrUpdatePerson');
  console.log('📋 Current newPerson state:', newPerson);
  console.log('📋 Client ID:', clientId);

  if (!clientId || isNaN(parseInt(clientId))) {
    console.error('❌ Invalid Client ID');
    setPersonErrors((prev) => ({ ...prev, server: 'Client ID is missing or invalid' }));
    return;
  }

  console.log('🔍 Validating form...');
  const isValid = validatePersonForm();
  console.log('🔍 Form validation result:', isValid);

  if (isValid) {
    try {
      const token = localStorage.getItem('token');
      console.log('🔑 Token found:', !!token);
      if (!token) throw new Error('No authentication token found');

      // The backend expects an ARRAY of license objects, not a string!
      // Use the driver licenses array directly
      const driverLicensesArray = newPerson.drivers_license_codes || [];
      
      console.log('📤 Driver licenses array for backend:', driverLicensesArray);

      // Build the data for the backend - use the FIELD NAME THE BACKEND EXPECTS
      const personData = {
        name: newPerson.name || '',
        surname: newPerson.surname || '',
        email: newPerson.email || '',
        designation: newPerson.designation || '',
        status: newPerson.status || 'Active',
        contact_number: newPerson.contact_number || '',
        id_passport_number: newPerson.id_passport_number || '',
        date_of_birth: newPerson.date_of_birth || '',
        // Backend expects 'drivers_license_codes' (plural) as an ARRAY
        drivers_license_codes: driverLicensesArray,
        clientId: parseInt(clientId),
      };

      console.log('📤 FINAL - Data being sent to server:', personData);

      let response;
      if (editingPersonId) {
        console.log('🔄 Updating person with ID:', editingPersonId);
        response = await axios.put(
          `${API_URL}/api/persons-of-interest/${editingPersonId}`,
          personData,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        console.log('➕ Creating new person');
        console.log('🌐 Making POST request to:', `${API_URL}/api/persons-of-interest`);
        
        try {
          response = await axios.post(
            `${API_URL}/api/persons-of-interest`,
            personData,
            { 
              headers: { 
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              timeout: 10000
            }
          );
          console.log('✅ POST Response status:', response.status);
          console.log('✅ POST Response data:', response.data);
        } catch (postError: any) {
          console.error('❌ POST Request failed:', postError);
          console.error('❌ POST Error response:', postError.response?.data);
          console.error('❌ POST Error status:', postError.response?.status);
          throw postError;
        }
      }

      console.log('✅ API Response:', response.data);

      // REFRESH THE ENTIRE LIST FROM SERVER
      await refreshPersonsList();

      // Reset form and close modal
      console.log('🗑️ Resetting form...');
      setNewPerson({
        id: 0,
        name: '',
        surname: '',
        email: '',
        designation: '',
        status: 'Active',
        contact_number: '+27',
        id_passport_number: '',
        date_of_birth: '',
        drivers_license_codes: [],
      });
      setPersonErrors((prev) => ({ ...prev, server: '' }));
      setEditingPersonId(null);
      
      console.log('✅ SUCCESS: Person added/updated successfully');
      
    } catch (error: any) {
      console.error('❌ Error in handleAddOrUpdatePerson:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Error message:', error.message);
      
      let errorMessage = `Failed to ${editingPersonId ? 'update' : 'add'} person of interest`;
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setPersonErrors((prev) => ({
        ...prev,
        server: errorMessage,
      }));
    }
  } else {
    console.log('❌ Form validation failed');
    console.log('❌ Current errors:', personErrors);
  }
  
  console.log('🏁 END: handleAddOrUpdatePerson');
};

  // Handle input changes
  const handlePersonInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target as { name: keyof PersonOfInterest; value: string };
    
    if (name === 'drivers_license_codes') return;

    // In handlePersonInputChange, update the contact number logic:
    if (name === 'contact_number') {
    // Always ensure it starts with +27
    let numberPart = value.startsWith('+27') ? value.slice(3) : value;
    numberPart = numberPart.replace(/[^0-9]/g, '');
    
    // Allow exactly 9 digits after +27 for South African numbers
    if (numberPart.length > 9) {
      numberPart = numberPart.slice(0, 9); // Trim to 9 digits
    }
    
    // Clear the error when user is typing
    setPersonErrors((prev) => ({
      ...prev,
      contact_number: '',
      server: '',
    }));
    
    // Always include +27 prefix
    const fullNumber = '+27' + numberPart;
    setNewPerson((prev) => ({
      ...prev,
      [name]: fullNumber,
    }));
  } else {
    // For other fields, update normally
    setNewPerson((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPersonErrors((prev) => ({ ...prev, [name]: '', server: '' }));
  }
  };

  // Handle driver's license codes change (for AddPersonModal)
    const handleDriverLicensesChange = (licenses: DriverLicense[]) => {
    
    // Ensure proper format and clean any JSON strings
    const cleanedLicenses = licenses.map(license => {
      let code = license.code;
      
      // If code is a JSON string like '{"EB"}', parse it
      if (typeof code === 'string' && code.startsWith('{') && code.endsWith('}')) {
        try {
          const parsed = JSON.parse(code);
          code = Object.values(parsed)[0] as string || code;
        } catch (error) {
          console.error('Error parsing license code:', error);
        }
      }
      
      return {
        code: code,
        dateOfFirstIssue: license.dateOfFirstIssue || '',
        expiresOn: license.expiresOn || ''
      };
    });
    
    setNewPerson(prev => ({
      ...prev,
      drivers_license_codes: cleanedLicenses
    }));
  };

  // Close person modal
  const closePersonModal = () => {
    setIsPersonModalOpen(false);
    setSelectedPerson(null);
  };

  // Handle view person
  const handleViewPerson = (personId: number) => {
    const person = personsOfInterest.find((p) => p.id === personId);
    if (person) {
      setSelectedPerson(person);
      setIsPersonModalOpen(true);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Person of Interest</h3>

        {can('person_of_interest', 'write') && (
          <button
            onClick={() => navigate(`/add-person/${clientId}`)}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            Add New Person
          </button>
        )}
      </div>

      {personErrors.server && (
        <p className="text-red-500 text-sm mb-4">
          {personErrors.server.includes('404')
            ? 'Persons of interest endpoint not found. Please check the backend.'
            : personErrors.server.includes('Server error')
            ? 'Server error: Please check the backend logs for details.'
            : personErrors.server}
        </p>
      )}
      {isLoadingPersons ? (
        <p className="text-gray-500">Loading...</p>
      ) : personsOfInterest.length > 0 ? (
        <div className="space-y-6 mb-6">
          {personsOfInterest.map((person: PersonOfInterest) => (
            <div key={person.id} className="border border-gray-200 rounded-md p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">Name</p>
                  <p className="text-sm text-gray-900">{person.name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Surname</p>
                  <p className="text-sm text-gray-900">{person.surname || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Email</p>
                  <p className="text-sm text-gray-900">{person.email || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Designation</p>
                  <p className="text-sm text-gray-900">{person.designation || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Status</p>
                  <p className="text-sm text-gray-900">{person.status || 'N/A'}</p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700">Date of Birth</p>
                  <p className="text-sm text-gray-900">{formatDisplayDate(person.date_of_birth)}</p>
                </div>

                {/* Add Driver's Licenses Section */}
                <div className="md:col-span-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">Driver's Licenses</p>
                  <div className="mt-1">
                    {person.drivers_license_codes && person.drivers_license_codes.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {person.drivers_license_codes.map((license, index) => {
                          
                          // Handle different data structures using the correct DriverLicense properties
                          let licenseCode, issueDate, expiresOn;
                          
                          if (typeof license === 'string') {
                            // If it's just a string code
                            licenseCode = license;
                            issueDate = null;
                            expiresOn = null;
                          } else if (license && typeof license === 'object') {
                            // Use the correct properties from DriverLicense type
                            licenseCode = license.code;
                            issueDate = license.dateOfFirstIssue;
                            expiresOn = license.expiresOn; // ADD THIS LINE
                          } else {
                            licenseCode = 'Unknown';
                            issueDate = null;
                            expiresOn = null;
                          }
                          
                          return (
                            <div 
                              key={index} 
                              className="bg-blue-100 border border-blue-200 text-blue-800 px-3 py-2 rounded-md text-sm flex items-center gap-2"
                            >
                              <span className="font-bold">{licenseCode}</span>
                              {issueDate && (
                                <span className="text-blue-600 text-xs">
                                  (Issued: {new Date(issueDate).toLocaleDateString()})
                                </span>
                              )}
                              {expiresOn && ( // ADD THIS SECTION FOR EXPIRY DATE
                                <span className="text-red-600 text-xs">
                                  (Expires: {new Date(expiresOn).toLocaleDateString()})
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No driver's licenses added</p>
                    )}
                  </div>
                </div>

              </div>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => handleViewPerson(person.id)}
                  className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm"
                >
                  View
                </button>

                {can('person_of_interest', 'write') && (
                  <button
                    onClick={() => navigate(`/add-person/${clientId}?editId=${person.id}`)}
                    className="px-3 py-1 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 text-sm"
                  >
                    Edit
                  </button>
                )}

              </div>

            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500 mb-6">No persons of interest added yet.</p>
      )}
      {isPersonModalOpen && <PersonModal person={selectedPerson} onClose={closePersonModal} />}
      
    </div>
  );
};

export default PersonOfInterestTab;